<template>
  <div class="col-lg-10 mx-auto">
    <header class="text-center mb-4">
      <workflow-navigation workflowStage="Test" />
      <h2 class="skin-page-heading">
        Let's see what happens when things don't go to plan
      </h2>
      <p class="col-sm-8 mx-auto">
        See if it still works if things don't go to plan, if anything is a
        concern, press solve, if not just leave it.
      </p>
    </header>

    <everything-you-want />

    <div v-if="showGuaranteedIncomeDialog" class="row">
      <div class="col"></div>
      <div>
        <all-scenarios-guaranteed-income @subscribed-to-offer="onSubscribedToGuaranteedIncome" />
      </div>
      <div class="col"></div>
    </div>

    <div class="row">
      <div class="col-md-12">
        <div class="row">
          <div class="col-lg-6">
            <scenario ref="lowerGrowthScenario" :scenario="ScenarioType.LowerGrowth" number="1" :solveEnabled="true"
              @solve-take-less="onSolveTakeLess" @solve-pay-more="onSolvePayMore" @solve-equity-release="onSolveEquityRelease" />
          </div>
          <div class="col-lg-6">
            <scenario ref="liveLongerScenario" :scenario="ScenarioType.LiveThreeYearsLonger" number="2" :solveEnabled="true"
              @solve-take-less="onSolveTakeLess" @solve-pay-more="onSolvePayMore" @solve-equity-release="onSolveEquityRelease" />
          </div>
        </div>
        <div class="row">
          <div class="col-lg-6">
            <scenario ref="potsFallScenario" :scenario="ScenarioType.PotsFallTwentyPercent" number="3" :solveEnabled="true"
              @solve-take-less="onSolveTakeLess" @solve-pay-more="onSolvePayMore" @solve-equity-release="onSolveEquityRelease" />
          </div>
          <div class="col-lg-6">
            <scenario ref="careCostsScenario" :scenario="ScenarioType.NeedExtraMoneyForLastTwoYears" number="4" :solveEnabled="true"
              @solve-take-less="onSolveTakeLess" @solve-pay-more="onSolvePayMore" @solve-equity-release="onSolveEquityRelease" />
          </div>
        </div>
      </div>
    </div>

    <div class="row my-4">
      <div class="col">
        <p class="text-center">
          Find out more detail on how we calculate your tests.
          <help ref="howWeCalculateTestsHelp" title="How we calculate your tests" :text="howWeCalcTestsText" :isWide="true" />
        </p>
      </div>
    </div>

    <page-nav ref="pageNav" backLink="design" @nextClicked="onNextClicked" :parent-will-handle-next="true" :next-link="nextLink" />

    <contact-us class="mt-5"></contact-us>
  </div>

  <shortfall-solver ref="shortfallSolver" @closing="onShortfallSolverClosing" />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from "vue";
import { useStore } from "vuex";
import View from "../ts/view";
import ScenarioType from "../ts/scenarioType";
import ScenarioOutcome from "../ts/scenarioOutcome";
import HelpText from "@/guiide/ts/helpText";
import { workflow } from "../ts/workflow/workflow";
import WorkflowNavigation from "@/guiide/components/common/WorkflowNavigation.vue";
import EverythingYouWant from "@/guiide/components/design/EverythingYouWant.vue";
import Scenario from "@/guiide/components/scenarios/Scenario.vue";
import AllScenariosGuaranteedIncome from "@/guiide/components/scenarios/AllScenariosGuaranteedIncome.vue";
import ContactUs from "./common/ContactUs.vue";
import PageNav from "@/guiide/components/common/PageNav.vue";
import Help from "@/guiide/components/common/Help.vue";
import ShortfallSolver from "@/guiide/components/scenarios/ShortfallSolver.vue";
import Constants from "../ts/constants";
import { numberToWord } from "../ts/utilities";

type FocusKey = ScenarioType | "HowWeCalcTestsHelp" | "NavNextButton";

const store = useStore();
const userJustSubscribedToGuaranteedIncome = ref(false);
const lowerGrowthScenario = ref(null);
const liveLongerScenario = ref(null);
const potsFallScenario = ref(null);
const careCostsScenario = ref(null);
const shortfallSolver = ref(null);
const howWeCalculateTestsHelp = ref(null);
const pageNav = ref(null);
const nextLink = ref("");
const focusables = new Map<FocusKey, any>();

onMounted(async () => {
  const nextWorkflowStep = await workflow.getNextWorkflowStep(View.Test);
  nextLink.value = nextWorkflowStep.resource;
  initialiseFocusablesMap();
});

const showGuaranteedIncomeDialog = computed<boolean>(() => {
  if (!store.state.configuration.guaranteedIncomeEnabled || !store.state.configuration.testDisplaysGuaranteedIncome) {
    return false;
  }

  if (userJustSubscribedToGuaranteedIncome.value) {
    return true;
  }

  if (!hasPensionPots.value) {
    return false;
  }

  const userSubscribedToGI = store.state.retiree?.guaranteedIncome.subscribed ?? false;
  const someTestsFailed = haveAnyTestsOfInterestFailed.value;
  return !userSubscribedToGI && someTestsFailed;
});

const haveAnyTestsOfInterestFailed = computed<boolean>(() => {
  return (
    store.state.youLiveLongerScenario.outcome ==
    ScenarioOutcome.Fail ||
    store.state.marketCrashScenario.outcome == ScenarioOutcome.Fail ||
    store.state.lowerGrowthScenario.outcome == ScenarioOutcome.Fail
  );
});

const hasPensionPots = computed<boolean>(() => {
  return store.state.retiree?.pensionPots.pots.length > 0;
});

const howWeCalcTestsText = computed<string[]>(() => {
  let text = HelpText.howWeCalcTests();
  const extraYearsForLiveLongerTest = store.state.configuration.defaultExtraYearsForLiveLongerTest;
  const extraYearsWord = numberToWord(extraYearsForLiveLongerTest);

  for (let i = 0; i < text.length; i++) {
    text[i] = text[i].replace(Constants.HelpTextTokens.numberOfExtraYearsInLiveLongerTest, extraYearsWord);
  }

  return text;
});

function onSolveTakeLess(scenarioType: ScenarioType) {
  shortfallSolver.value.checkCouldSolveByTakeLess(scenarioType);
}

function onSolvePayMore(scenarioType: ScenarioType) {
  shortfallSolver.value.checkCouldSolveByPayingMore(scenarioType);
}

function onSolveEquityRelease(scenarioType: ScenarioType) {
  shortfallSolver.value.checkCouldSolveByEquityRelease(scenarioType);
}

function onSubscribedToGuaranteedIncome() {
  userJustSubscribedToGuaranteedIncome.value = true;
}

function onShortfallSolverClosing(eventArgs: { scenario: ScenarioType, implemented: boolean }) {
  if (!eventArgs.implemented) {
    return;
  }

  nextTick(() => {
    const focusKeys = getFocusFallbackKeys(eventArgs.scenario);
    for (const key of focusKeys) {
      const focusable = focusables.get(key);
      const tookFocus: boolean = focusable.takeFocus("");
      if (tookFocus) {
        return;
      }
    }
  });
}

async function onNextClicked() {
  await workflow.goToNextWorkflowStep();
}

function getFocusFallbackKeys(current: FocusKey): FocusKey[] {
  const order: FocusKey[] = [ 
    ScenarioType.LowerGrowth, 
    ScenarioType.LiveThreeYearsLonger, 
    ScenarioType.PotsFallTwentyPercent, 
    ScenarioType.NeedExtraMoneyForLastTwoYears, 
    "HowWeCalcTestsHelp",
    "NavNextButton"
  ];
  const index = order.indexOf(current);
  return index >= 0 ? order.slice(index) : [];
}

function initialiseFocusablesMap() {
  focusables.set(ScenarioType.LowerGrowth, lowerGrowthScenario.value);
  focusables.set(ScenarioType.LiveThreeYearsLonger, liveLongerScenario.value);
  focusables.set(ScenarioType.PotsFallTwentyPercent, potsFallScenario.value);
  focusables.set(ScenarioType.NeedExtraMoneyForLastTwoYears, liveLongerScenario.value);
  focusables.set("HowWeCalcTestsHelp", howWeCalculateTestsHelp.value);
  focusables.set("NavNextButton", pageNav.value);
}

</script>
